// AllDayRow — ряд all-day событий над временной сеткой (M5, S2; §7/§8/§16 W3-T2..T4).
// Позиции чипов — из allDaySpans: день — leftPct=0/widthPct=100; неделя — span колонок.
// Больше MAX_ALLDAY_ROW_VISIBLE чипов → «+N ещё» → существующий DayEventsPage
// (дата — days[0]: в day-виде это сам день, в week-виде — начало диапазона; KISS, W3-заметки).
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { CalendarEvent } from '@/api/calendar';
import { allDaySpans, MAX_ALLDAY_ROW_VISIBLE } from '@/lib/timeGridLayout';
import type { AllDayPosition } from '@/lib/timeGridLayout';

/** Высота под-ряда all-day чипа (px): 24px — Material min touch target (как MIN_EVENT_HEIGHT_PX). */
const ALLDAY_CHIP_HEIGHT_PX = 24;

interface AllDayRowProps {
  /** Локальные дни диапазона (1 — «День», 7 — «Неделя»). */
  days: Date[];
  /** События диапазона (all-day отбираются в allDaySpans). */
  events: CalendarEvent[];
  /** Колбэк клика по чипу (переход к detail события). */
  onEventClick?: ((event: CalendarEvent) => void) | undefined;
}

/** Чип all-day события: span по колонкам и под-ряд по вертикали (top = index * 24px). */
function AllDayChip({
  position,
  row,
  onEventClick,
}: {
  position: AllDayPosition;
  row: number;
  onEventClick?: ((event: CalendarEvent) => void) | undefined;
}): JSX.Element {
  return (
    <Box
      data-testid="all-day-chip"
      onClick={(ev) => {
        ev.stopPropagation();
        onEventClick?.(position.event);
      }}
      sx={{
        position: 'absolute',
        top: row * ALLDAY_CHIP_HEIGHT_PX,
        left: `${position.leftPct}%`,
        width: `${position.widthPct}%`,
        height: ALLDAY_CHIP_HEIGHT_PX - 1,
        boxSizing: 'border-box',
        px: 0.5,
        py: 0.25,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 0.5,
        overflow: 'hidden',
        cursor: onEventClick ? 'pointer' : 'default',
      }}
    >
      <Typography noWrap sx={{ fontSize: '0.6875rem', lineHeight: 1.2 }}>
        {position.event.title}
      </Typography>
    </Box>
  );
}

/** Ряд all-day событий (M5) с «+N ещё» (S2). Пустой ряд — null (незачем занимать высоту). */
export function AllDayRow({ days, events, onEventClick }: AllDayRowProps): JSX.Element | null {
  const navigate = useNavigate();
  const spans = allDaySpans(events, days);
  if (spans.length === 0) return null;
  const visible = spans.slice(0, MAX_ALLDAY_ROW_VISIBLE);
  const hidden = spans.slice(MAX_ALLDAY_ROW_VISIBLE);
  return (
    <Box
      data-testid="all-day-row"
      sx={{
        position: 'relative',
        height: (visible.length + (hidden.length > 0 ? 1 : 0)) * ALLDAY_CHIP_HEIGHT_PX,
        width: '100%',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {visible.map((position, row) => (
        <AllDayChip key={position.event.id} position={position} row={row} onEventClick={onEventClick} />
      ))}
      {hidden.length > 0 ? (
        <Button
          data-testid="all-day-more"
          size="small"
          onClick={() => navigate(`/calendar/day/${format(days[0] as Date, 'yyyy-MM-dd', { locale: ru })}`)}
          sx={{
            position: 'absolute',
            top: visible.length * ALLDAY_CHIP_HEIGHT_PX,
            left: 0,
            right: 0,
            minHeight: ALLDAY_CHIP_HEIGHT_PX - 4,
            justifyContent: 'flex-start',
            px: 1,
            textTransform: 'none',
          }}
        >
          +{hidden.length} ещё
        </Button>
      ) : null}
    </Box>
  );
}
